/* OVR — ported from assets/js/main.js.
   The hero entrance, the scroll reveals, the snapping rails, add-to-cart.
   Cart, search and PDP stay with Horizon, which already owns them.

   Two rules this file must obey, both learned the hard way:

   1. NOTHING here may interfere with the theme editor. Shopify calls
      preventDefault() on every link click inside the editor preview so the
      merchant does not navigate away while editing. Any handler that reacts to
      a cancelled click will therefore fire on every single click and make the
      editor unusable. Interaction handlers are skipped entirely in design mode.

   2. A throw must never leave the page invisible. `.rev` starts at opacity 0
      and is revealed by script, so if this file errors early the whole page
      stays blank. Every step is isolated, reveals run first, and a timer
      un-hides anything still hidden. */
(function () {
  if (window.__ovrInit) return;
  window.__ovrInit = true;

  var DESIGN = !!(window.Shopify && window.Shopify.designMode);

  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE = 'cubic-bezier(.22,1,.36,1)';

  function safe(fn) {
    try { fn(); } catch (err) { console.error('[ovr] ' + fn.name + ' failed', err); }
  }

  /* Reveal on scroll. Under reduced motion, or in the editor, everything is
     simply shown — a section a merchant has just added must never sit at
     opacity 0 waiting for a scroll that will not come. */
  function reveals() {
    const items = $$('.ovr .rev');
    if (DESIGN || REDUCED || !('IntersectionObserver' in window)) {
      items.forEach(el => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        e.target.classList.add('in');
        obs.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    items.forEach(el => io.observe(el));
  }

  /* Last line of defence: whatever else happened, nothing stays invisible. */
  function failsafe() {
    setTimeout(function () {
      $$('.ovr .rev:not(.in)').forEach(el => el.classList.add('in'));
    }, 2000);
  }

  /* Hero entrance: the image settles out of a slow push-in, then the copy
     arrives under it. */
  function heroIn() {
    const hero = $('[data-hero]');
    if (!hero) return;
    const parts = $$('[data-hero-part]', hero);
    const img = $('[data-hero-img]', hero);

    if (DESIGN || REDUCED) {
      parts.forEach(p => (p.style.opacity = 1));
      return;
    }
    if (img) {
      img.animate(
        [{ opacity: 0, transform: 'scale(1.035)' },
         { opacity: 1, transform: 'none' }],
        { duration: 1200, easing: EASE, fill: 'both' }
      );
    }
    parts.forEach((p, i) => {
      p.animate(
        [{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }],
        { duration: 700, delay: 420 + i * 90, easing: EASE, fill: 'both' }
      );
    });
  }

  /* Snapping product rails. The bar is a scroll indicator, not decoration. */
  function rails() {
    $$('[data-rail]').forEach(root => {
      if (root.dataset.ovrBound) return;
      root.dataset.ovrBound = '1';

      const track = $('.rail__track', root);
      if (!track) return;
      const prev = $('[data-rail-prev]', root);
      const next = $('[data-rail-next]', root);
      const fill = $('[data-rail-fill]', root);

      const overflow = () => track.scrollWidth - track.clientWidth;

      function update() {
        const max = overflow();
        root.classList.toggle('is-static', max < 4);
        if (max < 4) return;
        const frac = track.clientWidth / track.scrollWidth;
        if (fill) {
          fill.style.width = (frac * 100) + '%';
          fill.style.left = (track.scrollLeft / track.scrollWidth * 100) + '%';
        }
        if (prev) prev.disabled = track.scrollLeft < 4;
        if (next) next.disabled = track.scrollLeft > max - 4;
      }

      const page = dir => {
        const card = $('.card', track);
        const step = card
          ? Math.max(1, Math.floor(track.clientWidth / (card.offsetWidth + 16))) * (card.offsetWidth + 16)
          : track.clientWidth * 0.85;
        track.scrollBy({ left: dir * step, behavior: REDUCED ? 'auto' : 'smooth' });
      };

      prev && prev.addEventListener('click', () => page(-1));
      next && next.addEventListener('click', () => page(1));
      track.addEventListener('scroll', update, { passive: true });
      addEventListener('resize', update);
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();
    });
  }

  /* Size chips post straight to Shopify's cart. Never in the editor: adding to
     a cart from a preview is not what anyone is trying to do there. */
  function adds() {
    if (DESIGN) return;
    document.addEventListener('click', async e => {
      const chip = e.target.closest('.ovr [data-variant]');
      if (!chip || chip.disabled) return;
      e.preventDefault();
      e.stopPropagation();

      chip.classList.add('is-busy');
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(chip.dataset.variant), quantity: 1 }] })
        });
        if (!res.ok) throw new Error(await res.text());
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        const drawer = document.querySelector('cart-drawer-component, #cart-drawer');
        if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
        else window.location.href = '/cart';
      } catch (err) {
        console.error('[ovr] add to cart failed', err);
        window.location.href = '/cart';
      } finally {
        chip.classList.remove('is-busy');
      }
    });
  }

  /* A top-level menu item with a submenu opens on hover and Horizon's menu
     script cancels the click on it, so "Shop by category" could be hovered but
     never followed. This sends the browser to the href when the click has been
     cancelled.

     It MUST NOT run in the theme editor. Shopify cancels every link click in
     the preview, so this would read every one of them as swallowed and
     navigate away — which is exactly what made the editor impossible to use. */
  function parentLinks() {
    if (DESIGN) return;
    document.addEventListener('click', e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest('a.menu-list__link[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href === '#' || a.target === '_blank') return;
      if (e.defaultPrevented) window.location.assign(a.href);
    });
  }

  /* Reveals first: if anything below throws, the page is still visible. */
  function boot() {
    safe(reveals);
    safe(failsafe);
    safe(heroIn);
    safe(rails);
    safe(adds);
    safe(parentLinks);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The editor re-renders sections in place; re-show and re-bind what it replaced.
  document.addEventListener('shopify:section:load', () => {
    safe(reveals);
    safe(heroIn);
    safe(rails);
  });
})();
